<template>
	<transition name="slide-fade">
		<div
			class="app-notification-banner"
			:class="{
				'app-notification-banner--success' : isTypeSuccess,
				'app-notification-banner--warning' : isTypeWarning,
				'app-notification-banner--danger' : isTypeError
			}"
			v-if="visible"
		>
			<div class="app-notification-banner__section">
				<slot name="icon">
					<div class="app-notification-banner__icon">
						<svg
							v-if="isTypeSuccess"
							viewBox="0 0 9.405 7.001"
						>
							<path
								fill="#fff"
								d="M14.72,8.79l-4.29,4.3L8.78,11.44a1,1,0,1,0-1.41,1.41l2.35,2.36a1,1,0,0,0,1.41,0l5-5a1,1,0,1,0-1.41-1.42Z"
								transform="translate(-7.02 -8.499)"
							/>
						</svg>

						<svg
							v-if="isTypeWarning"
							viewBox="0 0 11.268 11.247"
						>
							<path
								fill="#fff"
								d="M13.082,9.636,10.541,7.1a3.851,3.851,0,0,0,.079-.787A4.312,4.312,0,0,0,4.486,2.406a.563.563,0,0,0-.163.911l2.446,2.44L5.757,6.769,3.316,4.323a.571.571,0,0,0-.911.163A4.312,4.312,0,0,0,6.341,10.62a3.851,3.851,0,0,0,.787-.079l2.536,2.541a.565.565,0,1,0,.8-.8L7.707,9.529a.562.562,0,0,0-.534-.146,3.306,3.306,0,0,1-.832.112A3.188,3.188,0,0,1,3.125,6.313a3.373,3.373,0,0,1,.045-.562l2.2,2.21a.562.562,0,0,0,.8,0l1.788-1.8a.562.562,0,0,0,0-.781L5.773,3.17a3.441,3.441,0,0,1,.562-.045A3.188,3.188,0,0,1,9.518,6.313a3.306,3.306,0,0,1-.112.832.562.562,0,0,0,.146.534l2.755,2.755a.565.565,0,1,0,.8-.8Z"
								transform="translate(-2.002 -2.002)"
							/>
						</svg>

						<svg
							v-if="isTypeError"
							viewBox="0 0 10.392 10.346"
						>
							<path
								fill="#fff"
								stroke="#fff"
								stroke-width="0.3px"
								d="M9.875,8.995l3.935-3.928a.627.627,0,0,0-.887-.887L8.994,8.114,5.066,4.18a.627.627,0,1,0-.887.887L8.114,8.995,4.179,12.923a.627.627,0,1,0,.887.887L8.994,9.875l3.928,3.935a.627.627,0,1,0,.887-.887Z"
								transform="translate(-3.798 -3.799)"
							/>
						</svg>
					</div>
				</slot>
			</div>

			<div class="app-notification-banner__section app-notification-banner__section--flex-grow">
				<p class="app-notification-banner__title">
					{{ $t(title) }}
				</p>

				<p>{{ $t(message) }}</p>
			</div>

			<button
				class="app-notification-banner__button"
				@click.stop="closeBanner"
			>
				&times;
			</button>
		</div>
	</transition>
</template>

<script>
import { mapState } from 'vuex'

export default {
	props: {
		visible: Boolean
	},

	watch: {
		visible (val) {
			if (!val) return
			if (this.timeout) clearTimeout(this.timeout)
			this.timeout = setTimeout(() => {
				this.closeBanner()
			}, 5000)
		}
	},

	computed: {
		title () {
			const titles = {
				success: 'success',
				warning: 'warning_watch_out',
				failed: 'error_something_wrong',
				invalid: 'action_invalid'
			}

			return titles[this.type]
		},

		isTypeSuccess () {
			return this.type === 'success'
		},

		isTypeWarning () {
			return this.type === 'warning'
		},

		isTypeError () {
			return this.type === 'invalid' || this.type === 'failed'
		},

		...mapState({
			message: state => state.banner.message,
			type: state => state.banner.type
		})
	},

	methods: {
		closeBanner () {
			this.$store.commit('TOGGLE_BANNER', { status: false })
		}
	}
}
</script>

<style lang="scss" scoped>
.app-notification-banner {
	position: fixed;
	display: inline-flex;
	align-items: center;
	background-color: #fff;
	border-left: 5px solid #fff;
	box-shadow: 0 0 6px $default-light;
	color: $default-light;
	border-radius: 3px;
	min-width: 30em;
	top: 2em;
	right: 2em;
	z-index: 999;

	&__section {
		padding: .8em;

		&--flex-grow {
			flex: 1 1 auto;
		}
	}

	&__icon {
		border-radius: 50%;
		height: 1.6em;
		width: 1.6em;
		padding: .2em;
		display: flex;
		align-items: center;
		justify-content: center;

		svg {
			width: .8em;
			height: .8em;
		}
	}

	&__title { margin-bottom: .5em; }

	&__button {
		color: inherit;
		border: none;
		background: transparent;
		outline: none;
		cursor: pointer;
		font-size: 2em;
		padding: 0;
		margin: .8em;

		&:hover { color: $default; }
	}

	&--success { border-color: $primary; }
	&--success &__icon { background-color: $primary}
	&--success &__title { color: $primary}

	&--warning { border-color: $warning; }
	&--warning &__icon { background-color: $warning}
	&--warning &__title { color: $warning}

	&--danger { border-color: $danger; }
	&--danger &__icon { background-color: $danger}
	&--danger &__title { color: $danger}
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all .5s ease;
  opacity: 1;
}

.slide-fade-enter, .slide-fade-leave-to
/* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateX(10px);
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/app-components/AppNotificationBanner.vue